import { useEffect, useState } from 'react';
import { api, type Campaign, type Comparison } from '../api.js';
import Icon from '../components/Icon.js';

/**
 * Comparison between two batches.
 *
 * For a number-intelligence company the useful question is not "what is the
 * number now", but "has it changed since last time". A number moving from
 * ANSWERED to NOT_ALLOCATED is a deactivation; the other way round, a
 * reactivation.
 */
export default function Compare() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [a, setA] = useState(0);
  const [b, setB] = useState(0);
  const [data, setData] = useState<Comparison | null>(null);
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.campaigns().then((c) => {
      setCampaigns(c);
      if (c[1]) { setA(c[1].id); setB(c[0]!.id); }
    }).catch((e) => setErr(e.message));
  }, []);

  async function compare() {
    setBusy(true); setErr(''); setData(null);
    try { setData(await api.compare(a, b)); }
    catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
    finally { setBusy(false); }
  }

  const rows = data
    ? (onlyChanged ? data.items.filter((i) => i.state === 'CHANGED') : data.items)
    : [];

  const label = (id: number) => campaigns.find((c) => c.id === id)?.name ?? `#${id}`;

  return (
    <>
      <h1>Batch comparison</h1>
      <p className="subtitle">
        The same numbers tested at different moments. The latest result from each campaign is used.
      </p>

      {err && <div className="err">{err}</div>}

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="row">
          <label style={{ margin: 0, minWidth: 240 }}><span>Reference (A) — the older test</span>
            <select value={a} onChange={(e) => setA(Number(e.target.value))}>
              <option value={0}>— choose —</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>#{c.id} · {c.name}</option>)}
            </select></label>
          <label style={{ margin: 0, minWidth: 240 }}><span>Compared (B) — the new test</span>
            <select value={b} onChange={(e) => setB(Number(e.target.value))}>
              <option value={0}>— choose —</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>#{c.id} · {c.name}</option>)}
            </select></label>
          <button className="primary" disabled={busy || !a || !b || a === b}
                  onClick={() => void compare()}>
            <Icon name="compare" />{busy ? 'Comparing…' : 'Compare'}
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="cards">
            <div className="stat"><div className="n">{data.changed}</div>
              <div className="k">changed state</div></div>
            <div className="stat"><div className="n">{data.unchanged}</div>
              <div className="k">unchanged</div></div>
            <div className="stat"><div className="n">{data.onlyInA}</div>
              <div className="k">only in {label(data.campaigns.a)}</div></div>
            <div className="stat"><div className="n">{data.onlyInB}</div>
              <div className="k">only in {label(data.campaigns.b)}</div></div>
          </div>

          {data.transitions.length > 0 && (
            <>
              <h2>Transitions</h2>
              <div className="table-wrap" style={{ marginBottom: 20, maxWidth: 520 }}>
                <table>
                  <thead><tr><th>From → To</th><th>Numbers</th></tr></thead>
                  <tbody>
                    {data.transitions.map((t) => (
                      <tr key={t.transition}>
                        <td className="num">{t.transition}</td>
                        <td className="num">{t.n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="row" style={{ marginBottom: 10 }}>
            <label style={{ margin: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={onlyChanged} style={{ width: 'auto' }}
                     onChange={(e) => setOnlyChanged(e.target.checked)} />
              <span style={{ margin: 0 }}>changed only</span>
            </label>
            <span className="muted">{rows.length} of {data.total}</span>
          </div>

          {rows.length === 0 ? <div className="card empty">Nothing to show.</div> : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Number</th>
                  <th>{label(data.campaigns.a)}</th>
                  <th>{label(data.campaigns.b)}</th>
                  <th>State</th>
                </tr></thead>
                <tbody>
                  {rows.map((i) => (
                    <tr key={i.msisdn}>
                      <td className="num">{i.msisdn}</td>
                      <td>{i.a ? <span className={`tag ${i.a}`}>{i.a}</span> : <span className="muted">—</span>}</td>
                      <td>{i.b ? <span className={`tag ${i.b}`}>{i.b}</span> : <span className="muted">—</span>}</td>
                      <td className={i.state === 'CHANGED' ? '' : 'muted'}>
                        {i.state === 'CHANGED' ? <strong>changed</strong> : i.state.replace(/_/g, ' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
